import { isoDateTime, z } from "../zod.js";
import { localeKeys } from "../locales.js";
import { Currency, Uuid } from "../identity/admin-schemas.js";

// Module 04 — Production execution: the contract DTOs for slice 04-A, the
// WORK-ORDER SPINE (spec 04 §2.1, §2.2, §2.4, §2.5, §2.15, §2.18 + the §5 rows
// 1–9, 11, 12, 14, 29, 30, 34). The floor surface (§5 rows 10, 13, 15, 16, 24,
// 25, 26, 28, 31) belongs to slice 04-B and the floor-truth surface (17–23, 27,
// 32) to 04-C; module 08 brings row 33. Their DTOs are absent, not stubbed —
// §5's own slice statement, and the same statement slice 05-A wrote.
//
// CONVENTIONS, carried from inventory/schemas.ts and waste/schemas.ts:
//
//  * EXACTNESS (D-041). Every quantity, percentage and ratio on this wire is a
//    DECIMAL STRING mirroring its column's numeric(p,s) — the exact text
//    Postgres returns — never a JSON number. A cable length is 3 dp and a
//    money decision is computed from it; a float in the request is precisely
//    what the exact-arithmetic doctrine keeps out of an append-only ledger.
//    The scale here is numeric(14,3), NOT module 06's numeric(18,6): spec 04
//    §2 fixes 3 dp for this module, and a quantity crossing into module 06 is
//    converted at the service boundary rather than by widening a column.
//  * Money is INTEGER MINOR UNITS (`z.number().int()`) plus `currency char(3)`,
//    never a float (the money convention).
//  * Lists answer `{items: [...], next_cursor}` — never a bare array (the
//    module-01 defect `catalogue-list-envelopes.int.test.ts` was built for).
//
// REDACTION IS ABSENCE HERE, NOT NULL — a deliberate divergence from module 06,
// stated because a reader will otherwise assume the platform has one rule.
// Module 06 serialises a redacted cost as `null` beside a `redacted: true`
// marker (spec 06 §7). Spec 04 §7 says the cost fields are "stripped", and
// M04-S12 and M04-S15 both assert, at the API response rather than the UI, that
// a `qc` / `floor_worker` session's `GET /v1/work-orders/:id` contains **no
// `*_minor` key at all**. A `null` would satisfy neither scenario as written.
// So every role-redacted field below is `.optional()` — present with a value
// for a caller holding `production.view_costs`, ABSENT otherwise — and the two
// walkthroughs can assert key absence, which is a stronger and more checkable
// statement than "the value was blanked".

// --- shared scalars -------------------------------------------------------

/** A `numeric(14,3)` quantity as its exact decimal text (D-041). Zero is
 * admitted: response fields legitimately carry it (`qty_good` on a released
 * WO, `qty_produced` before completion). */
export const ProductionQty = z
  .string()
  .regex(/^\d{1,11}(\.\d{1,3})?$/)
  .openapi({ example: "1000.000", description: "Exact decimal, ≤ 3 dp (numeric(14,3))" });

/** A strictly positive quantity — `qty_planned` (DB CHECK `> 0`) and the
 * office result's reported quantities. Without it `"0"` passes the contract and
 * dies at an unnamed DB CHECK as a 500. */
export const ProductionPositiveQty = z
  .string()
  .regex(/^(?=.*[1-9])\d{1,11}(\.\d{1,3})?$/)
  .openapi({
    example: "1000.000",
    description: "Exact decimal, ≤ 3 dp, strictly positive (numeric(14,3) CHECK > 0)",
  });

/** A signed quantity — a material issue (positive) or its return-to-stock
 * reversal (negative), and plan-vs-issued variances. */
export const ProductionSignedQty = z
  .string()
  .regex(/^-?\d{1,11}(\.\d{1,3})?$/)
  .openapi({ example: "-120.500", description: "Exact signed decimal, ≤ 3 dp (numeric(14,3))" });

/** A signed, NON-ZERO quantity — `material_issues.qty` (DB CHECK `<> 0`): an
 * issue of nothing is not an issue, and the sign is what distinguishes a
 * consumption from a return. */
export const ProductionNonZeroSignedQty = z
  .string()
  .regex(/^-?(?=.*[1-9])\d{1,11}(\.\d{1,3})?$/)
  .openapi({
    example: "1200.000",
    description: "Exact signed decimal ≠ 0, ≤ 3 dp — negative = return to stock",
  });

/** A `numeric(6,3)` percentage 0–100 as exact decimal text (D-041). */
export const ProductionPct = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,3})?$/)
  .refine((v) => Number(v) <= 100, { message: "must be ≤ 100" })
  .openapi({ example: "3.000", description: "Exact decimal percentage 0–100, ≤ 3 dp" });

/** A `numeric(14,6)` output ratio — output qty per unit of primary finished
 * qty (spec 04 §2.18); the `finished` row is always `1.000000`. */
export const ProductionOutputRatio = z
  .string()
  .regex(/^\d{1,8}(\.\d{1,6})?$/)
  .openapi({ example: "1.000000", description: "Exact decimal, ≤ 6 dp (numeric(14,6))" });

/** A `numeric(10,4)` planned run time per unit, in minutes (§2.2). */
export const ProductionMinutesPerUnit = z
  .string()
  .regex(/^\d{1,6}(\.\d{1,4})?$/)
  .openapi({ example: "0.2500", description: "Exact decimal minutes, ≤ 4 dp (numeric(10,4))" });

/** Integer minor units (money) — never a float (the money convention). Signed: a cost
 * credit (a machine that UNDER-claims, §9.9) posts a negative entry. */
const MinorMoney = z.number().int();
/** ISO-8601 business date in the facility timezone (`due_date`). */
const BusinessDate = z.string().openapi({ format: "date", example: "2026-09-15" });
const Timestamp = z.string().openapi({ format: "date-time" });
const Cursor = z.string().nullable();

/** Module 05 `waste_cause_codes.code` — validated at the SERVICE layer against
 * this tenant's catalog, never an FK (spec 04 §2.4's 2026-08-03 correction).
 * The shape is module 05's `WasteCode` grammar, restated rather than imported
 * so a change to module 05's catalog contract cannot silently widen what this
 * module accepts. */
export const ScrapReasonCode = z
  .string()
  .regex(/^[A-Z][A-Z0-9_]{1,47}$/)
  .openapi({
    example: "STRAND_BREAK",
    description: "Module 05 waste cause code (service-validated, not an FK)",
  });

/** Canonical lowercase Module 01 `uoms` code, snapshotted as text (§2). */
export const ProductionUom = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,15}$/)
  .openapi({ example: "m", description: "Canonical lowercase Module 01 UoM code" });

// --- closed vocabularies (spec 04 §2) --------------------------------------

export const WorkOrderStatus = z.enum([
  "draft",
  "released",
  "in_progress",
  "completed",
  "closed",
  "cancelled",
]);

export const WorkOrderOperationStatus = z.enum([
  "queued",
  "ready",
  "in_progress",
  "paused",
  "completed",
  "skipped",
]);

/**
 * How a material issue was recorded (§2.5).
 *
 * `manual` (endpoint 30) and `backflush` (§3.5) are slice 04-A's. `scan`
 * arrives with the floor in 04-B. `remnant_match` is UNREACHABLE until slice
 * 05-C — module 05's remnant registry has no table, so nothing can propose a
 * remnant to match — and it is published anyway because the enum IS the wire
 * contract: adding a member later is a contract change for no reason, and a
 * client that already knows the value can render "matched from an off-cut" the
 * day 05-C starts producing it.
 */
export const MaterialIssueMethod = z.enum(["scan", "backflush", "manual", "remnant_match"]);

export const WoCostType = z.enum(["material", "labor", "overhead", "waste_variance"]);

export const WoCostSourceType = z.enum([
  "material_issue",
  "labor_time_entry",
  "machine_time",
  "calibration_variance",
  "scrap_beyond_standard",
  "manual_adjustment",
]);

export const WoOutputKind = z.enum(["finished", "co_product", "by_product"]);

/**
 * Which capture path produced a quantity report (§2.4, amended — D-071 pt 3).
 *
 * `floor` — a device `qty_reported` event: carries a `source_event_id` AND a
 * `shift_instance_id`. `office` — an office-recorded operation result (§3.11,
 * endpoint 34), the paper-traveler path, which carries NEITHER, because an
 * entry keyed hours later by someone who was not on the line has no honest
 * shift and no honest worker. Rows with a NULL shift are invisible to the
 * scoreboard BY CONSTRUCTION rather than by a filter — which is what keeps the
 * §3.7 anti-gaming attribution un-fabricable. Every 04-A row is `office`.
 */
export const OperationCaptureSource = z.enum(["floor", "office"]);

/**
 * How a barrel's measured quantity was obtained (§2.8) — the scale, the
 * meter counter, or a tape measured by hand.
 *
 * Hoisted here from the calibration block below on 2026-08-05 (D-084) because
 * it is now a shared closed vocabulary rather than a response-only one: both
 * the floor `calibration_recorded` payload and e34's office `calibration`
 * object are built from {@link CalibrationCaptureShape}, which needs it before
 * the request schemas are declared.
 */
export const CalibrationMeasurementMethod = z.enum([
  "scale_weight",
  "meter_counter",
  "manual_tape",
]);

/**
 * THE MEASURED-BARREL CAPTURE FIELDS (§2.8), declared ONCE and worn by BOTH
 * capture paths (D-084): the floor `calibration_recorded` payload (§5.1) and
 * e34's optional `calibration` object (§3.11). They are the same physical
 * fact — a barrel, what the vendor-locked display claimed, what the scale
 * said, and optionally which stock unit it landed on — and the two wire
 * surfaces exist only because a tablet and a paper sheet reach the server
 * differently. Two independent field lists would drift, and the field they
 * would drift on is `measured_qty`, which D-025 makes the only number
 * costing, inventory and genealogy read.
 */
export const CalibrationCaptureShape = {
  /** The worker-visible barrel identifier, e.g. "B-0217". */
  batch_label: z.string().min(1).max(64),
  /** What the vendor-locked display said. */
  machine_claimed_qty: ProductionPositiveQty,
  /** Scale/meter-counter truth — the ONLY number costing, inventory and
   * genealogy ever read (D-025). */
  measured_qty: ProductionPositiveQty,
  uom: ProductionUom,
  measurement_method: CalibrationMeasurementMethod,
  /** The output barrel/bobbin this batch landed on — the platform QR canon
   * `…/q/su/{uuid}` or the §9.15 human serial, resolved server-side through
   * module 06's service surface. ABSENT is legal: a barrel can be measured
   * before it is labelled (§2.8's `output_stock_unit_id` is nullable). */
  output_stock_unit_scan_code: z.string().min(1).max(256).optional(),
} as const;

// --- §2.1 work_orders ------------------------------------------------------

/**
 * The sales order reference module 04 serves on a pegged work order (spec 04
 * §5 rows 2 and 3, D-391 ruling 3), rendered by module 09 under the reader's
 * own doors. `sales_order_id` and `line_no` are always served; `order_no` and
 * `customer_name` are ABSENT for a reader module 09's customer door refuses.
 */
export const WorkOrderSalesOrderLine = z
  .object({
    sales_order_id: Uuid,
    line_no: z.number().int().positive(),
    order_no: z.string().optional(),
    customer_name: z.string().optional(),
  })
  .openapi("WorkOrderSalesOrderLine");

export const WorkOrder = z
  .object({
    id: Uuid,
    /** `WO-{YYYY}-{5-digit seq}` — the number printed on the traveler, unique
     * per tenant, and the final-but-one tiebreak of the D-072 queue order. */
    wo_no: z.string().regex(/^WO-\d{4}-\d{5}$/),
    facility_id: Uuid,
    department_id: Uuid.nullable(),
    product_id: Uuid,
    bom_id: Uuid,
    routing_id: Uuid,
    /** The planned order this draft was converted from (spec 04 §3.9's
     * conversion seam, P3-H-3, D-389 ruling 4), or NULL for a work order a
     * person created. Written once, at the conversion's create. */
    planned_order_id: Uuid.nullable(),
    /** The sales order line this work order makes (MTO pegging), or NULL.
     * Set only at create, validated by module 09's peg port (P3-D PR-D6,
     * D-290); NULL on every work order created before then. */
    sales_order_line_id: Uuid.nullable(),
    /** The pegged line's sales order reference (D-391 ruling 3), present on
     * the list and the detail when `sales_order_line_id` is set. */
    sales_order_line: WorkOrderSalesOrderLine.optional(),
    qty_planned: ProductionPositiveQty,
    qty_good: ProductionQty,
    qty_scrap: ProductionQty,
    uom: ProductionUom,
    /** The product's assembly scrap as the order snapshotted it at create
     * (§2.1, D-405 ruling 3), the factor its material plan divides by; a
     * later product edit does not move it (D-407 ruling 5). */
    assembly_scrap_pct: ProductionPct,
    status: WorkOrderStatus,
    /** 1 = highest (§2.1 CHECK 1..5); the FIRST key of the D-072 queue order. */
    priority: z.number().int().min(1).max(5),
    due_date: BusinessDate.nullable(),
    /** Written by Module 03 dispatch — by NOTHING in P2, so uniformly null.
     * The D-072 queue reads it as key 2 anyway, which is what lets module 03
     * switch the schedule on without a change in this module. */
    scheduled_start_at: Timestamp.nullable(),
    scheduled_end_at: Timestamp.nullable(),
    actual_start_at: Timestamp.nullable(),
    actual_end_at: Timestamp.nullable(),
    released_at: Timestamp.nullable(),
    released_by: Uuid.nullable(),
    completed_at: Timestamp.nullable(),
    closed_at: Timestamp.nullable(),
    closed_by: Uuid.nullable(),
    cancelled_at: Timestamp.nullable(),
    cancelled_by: Uuid.nullable(),
    cancel_reason: z.string().nullable(),
    currency: Currency,
    /** The four accumulators (§2.1, §3.6). ABSENT — not null — for a caller
     * without `production.view_costs` (see the header note). In slice 04-A
     * `cost_labor_minor` is structurally 0: labor comes from closed
     * `labor_time_entries`, which the floor timer (04-B) writes and the office
     * path (§3.11) deliberately does not. */
    cost_material_minor: MinorMoney.optional(),
    cost_labor_minor: MinorMoney.optional(),
    cost_overhead_minor: MinorMoney.optional(),
    /** Signed; positive = worse than standard. */
    cost_waste_variance_minor: MinorMoney.optional(),
    notes: z.string().nullable(),
    created_by: Uuid,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("WorkOrder");

// --- §2.2 work_order_operations -------------------------------------------

export const WorkOrderOperation = z
  .object({
    id: Uuid,
    work_order_id: Uuid,
    seq_no: z.number().int().min(1),
    routing_step_id: Uuid,
    name: z.string(),
    work_center_id: Uuid,
    /** Module 08 `equipment` does not exist: null throughout P2, and the P2
     * machine grain is `work_center_id` (D-073). Endpoint 33 (assign machine)
     * ships with module 08 — not stubbed, absent. */
    machine_id: Uuid.nullable(),
    status: WorkOrderOperationStatus,
    qty_good: ProductionQty,
    qty_scrap: ProductionQty,
    /** SAP operation-scrap parity: the EXPECTED loss at this step. Scrap within
     * it is standard cost; only the excess becomes waste variance (§3.6). */
    operation_scrap_pct: ProductionPct,
    setup_minutes_planned: z.number().int(),
    run_minutes_per_unit_planned: ProductionMinutesPerUnit,
    setup_minutes_actual: z.number().int(),
    /** Summed WORKER minutes (crews sum) — the labor costing base. Structurally
     * 0 in slice 04-A: see `cost_labor_minor`. */
    run_minutes_actual: z.number().int(),
    /** Machine WALL CLOCK minus pauses and downtime overlap — the machine-hour
     * overhead base (§3.6). Never derived from labor minutes, so a 3-worker
     * crew does not absorb 3× overhead. */
    machine_minutes_actual: z.number().int(),
    backflush: z.boolean(),
    requires_measured_capture: z.boolean(),
    requires_serial_capture: z.boolean(),
    /** §2.2 (04-C-5). While true, `op_started` / `op_resumed` / `op_completed`
     * / `qty_reported` floor events are refused to the conflict queue with
     * reason `qc_hold`; downtime and material returns stay legal. Module 07 is
     * the only thing that can SET it (D-071) and does not exist, so it is false
     * throughout P2 — the column, the §3.2 rejection branch and e32's manual
     * release ship together so nothing can trip a guard with no producer. */
    qc_hold: z.boolean(),
    /** §3.2's REWORK CYCLE marker (04-C-5): non-null while this operation is
     * re-opened by a `defect_handoffs` row disposed `rework`. While it is set,
     * qty reports on the op default `is_rework = true` and labor entries land
     * `entry_kind = 'rework'`; re-completion clears it. A timestamp rather than
     * a boolean because a step can be reworked twice and "when" is the question
     * a supervisor asks. */
    rework_reopened_at: Timestamp.nullable(),
    /** The hand-off that re-opened it (§3.2), for the audit trail the
     * transition writes. Null unless `rework_reopened_at` is set. */
    rework_defect_handoff_id: Uuid.nullable(),
    /** Module 07 (quality) does not exist: null throughout P2 (D-071). */
    qc_control_point_id: Uuid.nullable(),
    /** Work-centre rate snapshots taken at WO creation, so a rate change six
     * weeks later cannot silently re-price work already done. Redacted with the
     * other money fields (§7). */
    labor_rate_minor_snapshot: MinorMoney.optional(),
    overhead_rate_minor_snapshot: MinorMoney.optional(),
    piece_rate_eligible: z.boolean(),
    scheduled_start_at: Timestamp.nullable(),
    scheduled_end_at: Timestamp.nullable(),
    actual_start_at: Timestamp.nullable(),
    actual_end_at: Timestamp.nullable(),
    skipped_by: Uuid.nullable(),
    skip_reason: z.string().nullable(),
  })
  .openapi("WorkOrderOperation");

// --- §2.4 operation_qty_reports -------------------------------------------

export const OperationQtyReport = z
  .object({
    id: Uuid,
    work_order_id: Uuid,
    wo_operation_id: Uuid,
    /** The principal CREDITED: the floor worker on a `floor` row, the office
     * principal who keyed the traveler on an `office` row. */
    worker_id: Uuid,
    capture_source: OperationCaptureSource,
    /** NOT NULL for `floor` rows, NULL for `office` rows — the database holds
     * the biconditional, not a convention (§2.4). */
    shift_instance_id: Uuid.nullable(),
    qty_good: ProductionQty,
    qty_scrap: ProductionQty,
    scrap_reason_code: ScrapReasonCode.nullable(),
    /** NULL on `office` rows: they are not device actions and have no event to
     * point at; their trail is the hash-chained `audit_log` row endpoint 34
     * writes. */
    source_event_id: Uuid.nullable(),
    is_rework: z.boolean(),
    reported_at: Timestamp,
  })
  .openapi("OperationQtyReport");

// --- §2.5 material_issues --------------------------------------------------

export const MaterialIssue = z
  .object({
    id: Uuid,
    work_order_id: Uuid,
    /** NULL = a WO-level issue not bound to a step. */
    wo_operation_id: Uuid.nullable(),
    component_product_id: Uuid,
    /** NULL = unplanned issue (no BOM line satisfied). */
    bom_line_id: Uuid.nullable(),
    lot_id: Uuid.nullable(),
    stock_unit_id: Uuid.nullable(),
    /** Negative = a return-to-stock reversal. */
    qty: ProductionNonZeroSignedQty,
    uom: ProductionUom,
    issue_method: MaterialIssueMethod,
    /** The moving-average cost MODULE 06 RETURNED at issue time — never a
     * number module 04 computed (D-065; M04-S15 asserts exactly this). Redacted
     * per §7. */
    unit_cost_minor: MinorMoney.optional(),
    currency: Currency,
    issued_by: Uuid,
    issued_at: Timestamp,
    reversal_of_issue_id: Uuid.nullable(),
    source_event_id: Uuid.nullable(),
  })
  .openapi("MaterialIssue");

/** One BOM component on the WO detail's Materials tab: what the pinned BOM
 * plans against what has actually been issued (§4 S2). */
export const WorkOrderMaterialLine = z
  .object({
    component_product_id: Uuid,
    bom_line_id: Uuid.nullable(),
    wo_operation_id: Uuid.nullable(),
    /** The unit `qty_planned`, `qty_issued` and `qty_variance` are ALL stated
     * in: the BOM line's own unit for a planned line, and the component's base
     * unit for an unplanned issue, which has no line to take one from. */
    uom: ProductionUom,
    qty_planned: ProductionQty,
    /** Net of returns, RESTATED IN `uom` (D-221) — `material_issues.qty` is
     * stored in whatever unit each issue named, and the floor draws copper in
     * kg against a BOM line authored in metres, so this is a converted sum
     * rather than a raw one. A fully returned component reads 0 rather than
     * disappearing. */
    qty_issued: ProductionSignedQty,
    /** `qty_issued − qty_planned`, both in `uom`; positive = over-issued. */
    qty_variance: ProductionSignedQty,
    /** `qty_planned` in `base_uom` — the only form comparable with a module-06
     * answer (ATP, levels, the ledger), and what the release panel's
     * availability check uses. `0.000` on an unplanned row, which has no plan. */
    qty_planned_base: ProductionQty,
    /** The component product's base UoM (spec 01 §2.2). */
    base_uom: ProductionUom,
    /** What module 06 holds for this line's demand, each hold's own quantity,
     * in `base_uom` (§3.5, D-405 ruling 7). `0.000` before release, after
     * completion, and once the line's first issue fulfilled its hold. */
    qty_reserved_base: ProductionQty,
    /** The part of a released or in-progress order's plan that is neither
     * issued nor held, in `base_uom`. An order released before P3-I-1 holds
     * its old plan, so its scrap allowance reads here and the Materials tab
     * marks it "Not reserved" (D-405 ruling 7). `0.000` on any other order
     * and on an optional line. */
    qty_not_reserved_base: ProductionQty,
    issued_cost_minor: MinorMoney.optional(),
  })
  .openapi("WorkOrderMaterialLine");

// --- §2.14 operation_serial_lot_captures (slice 04-C-3) --------------------

/** The two directions §2.14 names. `consumed` = this lot/unit went INTO the
 * run; `produced` = it came OUT of it. Only `produced` satisfies the §3.2
 * `requires_serial_capture` guard and only `produced` feeds §2.18. */
export const SerialLotDirection = z.enum(["consumed", "produced"]).openapi("SerialLotDirection");

/**
 * One §2.14 capture, as the S2 Genealogy tab renders it (e3). At least one of
 * `lot_id` / `stock_unit_id` / `serial_no` is non-null — the table's CHECK, so
 * a client can rely on it rather than defend against a row that identifies
 * nothing.
 *
 * NO MONEY here, so nothing in it is redacted (§7): a capture is a lineage
 * fact, and the whole row is visible to every caller who may read the WO.
 */
export const SerialLotCapture = z
  .object({
    id: Uuid,
    work_order_id: Uuid,
    wo_operation_id: Uuid,
    direction: SerialLotDirection,
    /** Module 06 lot, when the scan named one. */
    lot_id: Uuid.nullable(),
    /** Module 06 measured stock unit (barrel/bobbin/drum), when the scan named
     * one. Both this and `lot_id` are what the Genealogy tab deep-links into
     * module 06's trace view with. */
    stock_unit_id: Uuid.nullable(),
    /** The laser-engraved serial (§3.3 step 8), when the worker typed or
     * scanned one. */
    serial_no: z.string().nullable(),
    /** Snapshotted from the resolved module-06 node at capture time (§2.14's
     * D-085 pt 4 column). NULL means the capture named a bare serial with no
     * module-06 node behind it, so no product can be claimed and the row takes
     * no part in §2.18's override. */
    product_id: Uuid.nullable(),
    qty: ProductionQty,
    uom: ProductionUom,
    captured_by: Uuid,
    captured_at: Timestamp,
    /** Which hand captured it (§2.4/§2.8's grammar, applied here from birth —
     * D-085 pt 3): a floor device tap, or the §3.11 office traveler capture. */
    capture_source: OperationCaptureSource,
    /** NULL on an `office` row — it is not a device action and has no event to
     * point at; its trail is the hash-chained `audit_log` row e34 writes. */
    source_event_id: Uuid.nullable(),
  })
  .openapi("SerialLotCapture");

/**
 * The office half of the capture (§3.11's paper completion sheet, e34) — the
 * SAME physical fact as the floor's `serial_lot_captured` payload, keyed by a
 * human instead of scanned by one.
 *
 * It exists for the reason D-084 had to discover the hard way for calibration:
 * this sub-step also ships the §3.2 `requires_serial_capture` guard, and a
 * guard whose only satisfier is a device tap makes every office completion of a
 * serial-capture step a permanent 409. Shipping the escape in the same change
 * as the guard is that lesson applied once instead of twice (D-085 pt 3).
 */
export const OperationResultSerialCapture = z
  .object({
    direction: SerialLotDirection,
    /** §2.14's at-least-one rule, mirrored: a capture must identify something.
     * `scan_code` is the RAW scan string (§5.1's rule holds on both paths — the
     * server resolves it, the client never does). */
    serial_no: z.string().min(1).max(128).optional(),
    scan_code: z.string().min(1).max(256).optional(),
    qty: ProductionPositiveQty,
    uom: ProductionUom,
  })
  .refine((v) => v.serial_no !== undefined || v.scan_code !== undefined, {
    message: "at least one of serial_no / scan_code (spec 04 §2.14)",
  })
  .openapi("OperationResultSerialCapture");

// --- §2.15 wo_cost_entries -------------------------------------------------

export const WoCostEntry = z
  .object({
    id: Uuid,
    work_order_id: Uuid,
    wo_operation_id: Uuid.nullable(),
    cost_type: WoCostType,
    /** Signed; reversals and cost credits are negative. */
    amount_minor: MinorMoney,
    currency: Currency,
    source_type: WoCostSourceType,
    /** The id in the source table. No FK: sources span slices (a
     * `labor_time_entry` source lands with 04-B). */
    source_id: Uuid,
    entered_at: Timestamp,
    entered_by: Uuid,
  })
  .openapi("WoCostEntry");

/** The four accumulators as one object — the close response's breakdown and the
 * Costs tab's totals. Its equality with `SUM(wo_cost_entries)` per `cost_type`
 * is the §2.15 invariant M04-S15 asserts ON THE WIRE rather than trusting. */
export const WorkOrderCostSummary = z
  .object({
    material_minor: MinorMoney,
    /** Slice 04-A: an explicit 0. Labor is captured by the floor timer (04-B);
     * the office path writes no `labor_time_entries` by design (§3.11), so the
     * accumulator is structurally zero and is labelled as pending on the Costs
     * tab rather than shown as a computed-looking number. */
    labor_minor: MinorMoney,
    overhead_minor: MinorMoney,
    waste_variance_minor: MinorMoney,
    currency: Currency,
  })
  .openapi("WorkOrderCostSummary");

// --- §2.18 wo_outputs ------------------------------------------------------

export const WoOutput = z
  .object({
    id: Uuid,
    product_id: Uuid,
    kind: WoOutputKind,
    output_ratio: ProductionOutputRatio,
    /** NOT NULL for `finished`/`co_product`; Σ over those rows = 100.000
     * (service-enforced). NULL for a by-product, which is credited at NRV
     * instead of taking a share. */
    apportionment_pct: ProductionPct.nullable(),
    /** NOT NULL for `by_product`. Redacted with the other money fields (§7), so
     * it is `.optional()` AND `.nullable()`: absent means "you may not see it",
     * null means "this row is not a by-product". §2.18's Module 09 price-list
     * fallback is unavailable in P2 (D-071) — when the pinned BOM sets no
     * value, 0 is snapshotted and the Costs tab says the row has no NRV source,
     * naming Module 09. Never a silent 0. */
    nrv_minor_per_uom: MinorMoney.nullable().optional(),
    uom: ProductionUom,
    /** Filled at WO completion: `output_ratio × wo.qty_good`, overridden by
     * summed measured captures where they exist (measured truth beats ratio). */
    qty_produced: ProductionQty,
    currency: Currency,
  })
  .openapi("WoOutput");

/** What completion actually received into stock, per declared output — the
 * shape that also rides `production.work_order_completed` (§6). */
export const WoOutputReceipt = z
  .object({
    product_id: Uuid,
    kind: WoOutputKind,
    qty: ProductionQty,
    uom: ProductionUom,
    /** The lot module 06's `ReceiptsService.receiveProductionOutput` created
     * (D-065): the receipt is module-06-owned code called in the SAME
     * transaction, never an event. */
    lot_id: Uuid.nullable(),
    stock_unit_id: Uuid.nullable(),
    /** Finished + co-products take a share of the pool; by-products carry an
     * NRV credit instead. Both redacted per §7. */
    cost_share_minor: MinorMoney.nullable().optional(),
    nrv_credit_minor: MinorMoney.nullable().optional(),
  })
  .openapi("WoOutputReceipt");

// --- §5 row 4: release ------------------------------------------------------

/** One short component from the release-time ATP check (§3.1). Shortages do NOT
 * block the release — they ride `production.work_order_released` for Module 12
 * (which does not exist, D-071), and the release panel shows the shortfall
 * itself, which is the operator-facing half.
 *
 * ALL THREE QUANTITIES ARE IN `uom`, THE COMPONENT PRODUCT'S BASE UNIT (D-221).
 * `required` is therefore the BOM line's requirement CONVERTED, not the number
 * the line states: a BOM may be authored in any unit that converts to the
 * component's base (spec 01 §9.6), while `available` comes from module-06 ATP,
 * which only ever answers in base. The unit is on the wire because it was the
 * absence of it that let a metre be compared against a kilogram unnoticed. */
export const WorkOrderShortage = z
  .object({
    component_product_id: Uuid,
    uom: ProductionUom,
    required: ProductionQty,
    available: ProductionQty,
    shortfall: ProductionQty,
  })
  .openapi("WorkOrderShortage");

// --- requests ---------------------------------------------------------------

/**
 * Create a draft work order (§5 row 1).
 *
 * `planned_order_id` is DELIBERATELY ABSENT from this body. §3.9 creates a WO
 * from a planned order through the in-process service call module 03 makes
 * (`createWorkOrderFromPlannedOrder`), not over HTTP — and module 03 does not
 * exist, so an id sent here could be validated against nothing. Accepting it
 * would publish a field whose only possible value is unverifiable. The unique
 * partial index that makes a retried conversion idempotent is created by 04-A
 * (migration 0019) so module 03 adds a caller and no schema.
 *
 * `bom_id`/`routing_id` are optional: omitted, the service pins the product's
 * ACTIVE versions; supplied, an older version can be pinned deliberately (§4
 * S3's overridable picker, with its warning).
 */
export const WorkOrderCreateRequest = z
  .object({
    product_id: Uuid,
    qty_planned: ProductionPositiveQty,
    uom: ProductionUom,
    facility_id: Uuid,
    department_id: Uuid.optional(),
    bom_id: Uuid.optional(),
    routing_id: Uuid.optional(),
    due_date: BusinessDate.optional(),
    priority: z.number().int().min(1).max(5).optional(),
    /** Peg this work order to a sales order line (MTO, P3-D PR-D6, D-290
     * ruling 6). Module 09's peg port validates it inside the create
     * transaction: the line must exist in this tenant, its order must be
     * confirmed, in production or ready, the line open or in production, and
     * the line's product this work order's product. Set once, at create. */
    sales_order_line_id: Uuid.optional(),
    notes: z.string().max(4000).optional(),
  })
  .openapi("WorkOrderCreateRequest");

/** Edit a draft (§5 row 8). `priority` stays editable through `released` — the
 * dispatch board's drag-to-reorder writes exactly this field. Everything else
 * is draft-only, service-enforced: the operations are a snapshot, and a live
 * WO's quantity is what the floor has already been told to make. */
export const WorkOrderPatchRequest = z
  .object({
    qty_planned: ProductionPositiveQty.optional(),
    due_date: BusinessDate.nullable().optional(),
    priority: z.number().int().min(1).max(5).optional(),
    notes: z.string().max(4000).nullable().optional(),
  })
  .openapi("WorkOrderPatchRequest");

export const WorkOrderReleaseRequest = z
  .object({
    /** The §4 S3 "release anyway" confirmation. Without it a release that finds
     * shortages is refused (`WO_RELEASE_SHORTAGES_UNACKNOWLEDGED`) so a planner
     * cannot release short by accident; with it the release proceeds and the
     * shortages ride the event. */
    acknowledge_shortages: z.boolean().optional(),
  })
  .openapi("WorkOrderReleaseRequest");

/**
 * Cancel (§5 row 7). `partial_disposition` satisfies the §3.1 in_progress
 * cancel guard IN ONE CALL: every produced-but-unreceived good quantity is
 * either received to stock or explicitly scrapped, in the same transaction as
 * the cancel. Without it a mid-run cancel would leave orphaned WIP value —
 * §9.12, and the reason the guard exists.
 *
 * `material_disposition` (added 2026-08-05, slice 04-B-4 — D-076 pt 4 ships
 * BOTH §9.12 material exits together): `scrap` writes EVERY still-issued
 * remainder off `production → scrap` in the cancel's transaction, one
 * module-06 movement per original issue at its captured cost. The other exit
 * is returning the material first (e30 negative qty / the floor's S10
 * return); without either, a cancel with unreturned material answers 409
 * `WO_CANCEL_DISPOSITION_REQUIRED` naming the remaining net quantities.
 */
export const WorkOrderCancelRequest = z
  .object({
    reason: z.string().min(1).max(2000),
    partial_disposition: z
      .object({
        receive_to_stock_qty: ProductionQty.optional(),
        scrap_qty: ProductionQty.optional(),
        scrap_reason_code: ScrapReasonCode.optional(),
      })
      .optional(),
    material_disposition: z
      .object({
        action: z.literal("scrap"),
        scrap_reason_code: ScrapReasonCode,
      })
      .optional(),
  })
  .openapi("WorkOrderCancelRequest");

export const OperationSkipRequest = z
  .object({
    /** Required (§5 row 9): "steel shielding not needed for this cable spec" is
     * the record of why a step the routing planned never ran. */
    reason: z.string().min(1).max(2000),
  })
  .openapi("OperationSkipRequest");

/**
 * Record an operation result FROM THE OFFICE — the paper-traveler path (§3.11,
 * endpoint 34, added 2026-08-03 under D-071 pt 3).
 *
 * It exists because KazKabel runs paper travelers today and module 02's go-live
 * checklist treats `channels.floor_pwa_tested` as a gate a tenant passes AFTER
 * going live: a work order that can only be advanced by a device is one most
 * tenants cannot advance on their first day, and one every tenant loses when
 * the tablets are flat. It is permanent product, not slice scaffolding.
 *
 * What it writes: one `operation_qty_reports` row with `capture_source='office'`
 * (none when a Complete reports zero good and zero scrap on a step that already
 * carries a report, D-299 owner ruling W1), the operation projection,
 * `machine_minutes_actual` from the recorded wall clock, the §3.6 overhead +
 * scrap-beyond-standard cost entries, the §3.5 backflush when the step is
 * flagged, the WO rollups, an `audit_log` row naming the office principal, and
 * `production.operation_completed` on `complete:true`.
 *
 * What it deliberately does NOT write — the load-bearing half: no
 * `operation_events` row, no `labor_time_entries` (so the labor accumulator is
 * structurally 0 in 04-A and is LABELLED as pending rather than shown as a
 * computed zero), no `piece_rate_entries`, and no shift attribution, therefore
 * no scoreboard row.
 */
/**
 * The measured barrel that rides an office-recorded result (§3.11, §3.3 step 7
 * — added 2026-08-05, D-084).
 *
 * §3.11's paper completion sheet ALREADY carries these numbers: the operator
 * writes down what the extruder claimed and what the scale said, and the
 * office keys the sheet. Without this object the §3.2 `requires_measured_capture`
 * guard was unsatisfiable from the office, so an extruding work order whose
 * station has no working tablet could be started and never completed — the
 * gap deployed staging caught on M04-S15.
 *
 * It is NOT a floor bypass: the record it writes is stamped
 * `capture_source='office'` with a NULL `source_event_id` (§2.8's iff-CHECK,
 * migration 0026), so the office capture is distinguishable from a device tap
 * in the stored row forever, and NO `operation_events` row is minted — §3.11's
 * load-bearing half is untouched.
 */
export const OperationResultCalibration = z
  .object(CalibrationCaptureShape)
  .openapi("OperationResultCalibration");

export const OperationResultRequest = z
  .object({
    /** Zero good AND zero scrap is refused 422 `VALIDATION_FAILED`, except on
     * `complete: true` for a step that already carries a report, which then
     * completes it and writes no report (D-299 owner ruling W1). */
    qty_good: ProductionQty,
    qty_scrap: ProductionQty,
    /** Required when `qty_scrap > 0` (service-enforced, exactly as §2.4 demands
     * of a floor report) and validated against Module 05
     * `waste_cause_codes.code`. */
    scrap_reason_code: ScrapReasonCode.optional(),
    /** Both given → `machine_minutes_actual` and therefore the §3.6 overhead
     * absorption. Neither given → the operation absorbs no overhead, which is
     * the honest answer when nobody recorded a clock. Either one later than
     * now is refused 422 (`OPERATION_START_AT_INVALID`, D-299 owner ruling W2;
     * `OPERATION_END_AT_INVALID`, ruling V3). */
    started_at: Timestamp.optional(),
    ended_at: Timestamp.optional(),
    complete: z.boolean(),
    note: z.string().max(2000).optional(),
    /**
     * The §3.3 step-7 barrel measurement, when the traveler carries one
     * (D-084). Recorded through the SAME engine as the floor's
     * `calibration_recorded` — same variance arithmetic, same §3.6 cost entry,
     * same module-06 measure correction (D-065), same emission.
     *
     * Supplying it SATISFIES the §3.2 measured-capture guard in the same
     * transaction as the completion, so a failed completion leaves no orphan
     * record. Supplying it for a step that does NOT require measured capture
     * is accepted and recorded anyway (D-084 ruling 2): the measured value is
     * real data, D-025 makes it the only number costing and inventory read
     * regardless of the flag, and a work centre only EARNS
     * `requires_measured_capture` after §3.8 detects drift — refusing
     * pre-flag measurements would starve the analysis that sets the flag.
     */
    calibration: OperationResultCalibration.optional(),
    /**
     * The §3.3 step-8 serial/lot captures the traveler carries (added
     * 2026-08-06, 04-C-3 — D-085 pt 3). An ARRAY, not a single object, because
     * unlike a barrel measurement a rolling step legitimately produces several
     * labelled units in one entry (M04-S13's 100 m cut into two sellable 50 m
     * drums is the design partner's own case), and forcing one call per drum
     * would make the office path lose facts the floor path can record.
     *
     * Supplying at least one `produced` capture SATISFIES the §3.2
     * `requires_serial_capture` guard in the same transaction as the
     * completion, so a failed completion leaves no orphan capture. Captures on
     * a step that does NOT require serial capture are accepted and recorded
     * anyway, for D-084 ruling 2's reason applied to lineage: a serial someone
     * engraved is a real fact, and refusing to record it because a flag is off
     * would throw away traceability nobody can reconstruct later.
     */
    serial_captures: z.array(OperationResultSerialCapture).max(50).optional(),
  })
  .openapi("OperationResultRequest");

/**
 * Office-side manual issue or return-to-stock (§5 row 30) — the API behind the
 * WO detail Materials tab. Negative `qty` = a return, and it MUST name the
 * issue it reverses (`reversal_of_issue_id`; the DB CHECK holds the same rule),
 * because a negative row that names nothing cannot be reconciled against the
 * movement that created it. The return is denominated in the ORIGINAL issue's
 * UoM and component (amended 2026-08-05, 04-B-4) and is costed at the
 * ORIGINAL's captured `unit_cost_minor` — never re-averaged (§9.14).
 *
 * The stock itself moves through module-06-owned `IssueService
 * .issueForWorkOrderInTxn` (issues) / `returnForWorkOrderInTxn` (returns —
 * one `material_return` movement, D-076) in the SAME transaction (D-065).
 * Module 04 holds no grant on any inventory table.
 */
export const MaterialIssueCreateRequest = z
  .object({
    component_product_id: Uuid,
    qty: ProductionNonZeroSignedQty,
    uom: ProductionUom,
    lot_id: Uuid.optional(),
    stock_unit_id: Uuid.optional(),
    wo_operation_id: Uuid.optional(),
    reversal_of_issue_id: Uuid.optional(),
  })
  .openapi("MaterialIssueCreateRequest");

/** Manual cost adjustment (§5 row 12) — finance only, reason required,
 * audit-chained. It appends a compensating `wo_cost_entries` row
 * (`source_type='manual_adjustment'`); the ledger is append-only, so a
 * correction is never an edit. */
export const WoCostAdjustmentRequest = z
  .object({
    cost_type: WoCostType,
    /** Signed minor units — a correction is as often a credit as a charge. */
    amount_minor: MinorMoney,
    wo_operation_id: Uuid.optional(),
    reason: z.string().min(1).max(2000),
  })
  .openapi("WoCostAdjustmentRequest");

// --- responses --------------------------------------------------------------

export const WorkOrderListResponse = z
  .object({ items: z.array(WorkOrder), next_cursor: Cursor })
  .openapi("WorkOrderListResponse");

/**
 * The WO detail (§5 row 3 / §4 S2).
 *
 * `costs` is ABSENT for a caller without `production.view_costs` — the whole
 * Costs tab is hidden, not emptied. The tabs a slice cannot fill are absent
 * from this payload for the same reason §4 gives for the tab strip: an empty
 * Calibration tab reads as "this WO had no drift", which is a different and
 * false statement. So there is no `events`, `genealogy` or `calibration` key
 * here — 04-B and 04-C add them with the tables behind them.
 */
export const WorkOrderDetailResponse = z
  .object({
    work_order: WorkOrder,
    operations: z.array(WorkOrderOperation),
    material_lines: z.array(WorkOrderMaterialLine),
    outputs: z.array(WoOutput),
    /** Every §2.4 quantity report on this WO, oldest first — §3.11's "the WO
     * detail shows which results were keyed": a report's `capture_source` +
     * `reported_at` is that answer in 04-A. The principal travels as
     * `worker_id`, a bare uuid — there is deliberately NO principal name here,
     * because no universally-held members endpoint exists to resolve one, and
     * a name only some roles could fetch would make the attribution
     * role-dependent. Quantities only — no money, so nothing in a report is
     * redacted (§7). */
    qty_reports: z.array(OperationQtyReport),
    /** Every §2.5 row, oldest first (added 2026-08-05, 04-B-4): the Materials
     * tab's return action must NAME the issue it reverses
     * (`reversal_of_issue_id`), and the per-component nets in `material_lines`
     * cannot name one. `unit_cost_minor` is redaction-by-absence per §7. */
    material_issues: z.array(MaterialIssue),
    /** Every §2.14 capture on this WO, oldest first (added 2026-08-06, 04-C-3
     * — ADDITIVE, so no `oasdiff` breaking change): the S2 Genealogy tab's
     * whole content. It is a plain array rather than a `genealogy` object
     * because the tab renders exactly this — consumed and produced rows with
     * deep links into module 06's trace view — and inventing a shape the table
     * cannot fill would be the empty-tab defect §4 forbids in a different
     * costume. */
    serial_lot_captures: z.array(SerialLotCapture),
    costs: WorkOrderCostSummary.optional(),
  })
  .openapi("WorkOrderDetailResponse");

export const WorkOrderReleaseResponse = z
  .object({ work_order: WorkOrder, shortages: z.array(WorkOrderShortage) })
  .openapi("WorkOrderReleaseResponse");

export const WorkOrderCompleteResponse = z
  .object({ work_order: WorkOrder, output_receipts: z.array(WoOutputReceipt) })
  .openapi("WorkOrderCompleteResponse");

export const WorkOrderCloseResponse = z
  .object({ work_order: WorkOrder, cost_breakdown: WorkOrderCostSummary })
  .openapi("WorkOrderCloseResponse");

export const WorkOrderResponse = z.object({ work_order: WorkOrder }).openapi("WorkOrderResponse");

export const WorkOrderOperationResponse = z
  .object({ operation: WorkOrderOperation })
  .openapi("WorkOrderOperationResponse");

export const OperationResultResponse = z
  .object({
    operation: WorkOrderOperation,
    /** The §2.4 row this call wrote. A Complete with zero good and zero scrap
     * on a step that already carries a report writes none (D-299 owner ruling
     * W1), and this is then the step's latest report on file. */
    qty_report: OperationQtyReport,
    /** The entries this call appended (§3.6): overhead from the recorded wall
     * clock, scrap beyond standard, and the backflush material issues' costs.
     * Absent-by-redaction is not a case here — the endpoint's grant is the
     * office record grant, and a caller without `production.view_costs` gets an
     * empty array rather than amounts. */
    cost_entries: z.array(WoCostEntry),
    /** The §2.14 captures this call wrote, in request order (04-C-3). Empty
     * when the traveler carried none — never absent, because "I recorded no
     * captures" and "I cannot tell you" are different answers and only one of
     * them is true here. */
    serial_lot_captures: z.array(SerialLotCapture),
  })
  .openapi("OperationResultResponse");

export const MaterialIssueResponse = z
  .object({ material_issue: MaterialIssue })
  .openapi("MaterialIssueResponse");

export const WoCostLedgerResponse = z
  .object({
    entries: z.array(WoCostEntry),
    totals: WorkOrderCostSummary,
    next_cursor: Cursor,
  })
  .openapi("WoCostLedgerResponse");

export const WoCostEntryResponse = z.object({ entry: WoCostEntry }).openapi("WoCostEntryResponse");

/** One card in a station's queue (§5 row 14). Deliberately NOT the full
 * operation: the floor renders a card, and every money field is stripped for a
 * floor session anyway. */
export const StationQueueOperation = z
  .object({
    id: Uuid,
    work_order_id: Uuid,
    wo_no: z.string(),
    seq_no: z.number().int(),
    product_id: Uuid,
    product_name: z.string(),
    qty_planned: ProductionQty,
    /** Good + scrap reported against this operation so far. */
    qty_reported: ProductionQty,
    /** The work order's base UoM (§9.13 fixes it at creation), added
     * 2026-08-06 with 04-C-3 — ADDITIVE. The floor's measured-capture and
     * serial-capture forms both need a unit for the number the worker reads off
     * a scale, and until now the operator TYPED it into every barrel: a
     * keystroke the server already knows the answer to, on a phone, in gloves,
     * where "m" and "M" and "мм" are all one slip apart. */
    uom: ProductionUom,
    /** The floor's ENTRY precision for `uom` (added 2026-08-08, D-091 —
     * ADDITIVE): module 01's `uoms.decimal_precision` for the snapshotted
     * code, CLAMPED to 3 because §2.4's quantities are `numeric(14,3)` and
     * `ProductionQty`'s own regex admits at most 3 fraction digits — a unit
     * declared finer than the wire cannot be entered because it cannot be
     * carried. The S10 stepper + numpad renders exactly this many fraction
     * digits (0 = integer-only, no decimal key at all); decimals are the
     * NORMAL case for cable lengths, so a snapshotted code that no longer
     * resolves in the catalogue serializes as 3, never 0. */
    uom_decimal_precision: z.number().int().min(0).max(3),
    status: WorkOrderOperationStatus,
    priority: z.number().int().min(1).max(5),
    due_date: BusinessDate.nullable(),
    /** The operation's own scheduled start, written by module 03's publish
     * (P3-I-2, D-399 ruling 3); null before the facility's first publish and
     * for an operation the published schedule did not place. */
    scheduled_start_at: Timestamp.nullable(),
    work_center_id: Uuid,
    backflush: z.boolean(),
    requires_measured_capture: z.boolean(),
    requires_serial_capture: z.boolean(),
    /** §2.2 (04-C-5) — the station card says the step is held BEFORE the
     * operator walks to it and taps Start into a refusal. False throughout P2:
     * module 07 sets it and does not exist (D-071). */
    qc_hold: z.boolean(),
    /** §3.2's rework re-open (04-C-5): non-null = this card is a RE-DO of work
     * already completed once, and the queue flags it as such. */
    rework_reopened_at: Timestamp.nullable(),
  })
  .openapi("StationQueueOperation");

export const StationQueueResponse = z
  .object({
    /** In the station-queue order (spec 04 §3.3, D-072, D-399 ruling 4) — see the endpoint description. */
    operations: z.array(StationQueueOperation),
    /** Opaque marker the floor cache passes back to fetch only what changed
     * (§5 row 14). Published in 04-A because the office dispatch board is the
     * queue's first consumer; the offline device that also uses it is 04-B's. */
    delta_cursor: z.string(),
  })
  .openapi("StationQueueResponse");

// --- shift calendar (spec 04 §2.9, §5 row 24 — slice 04-B-1) ---------------
//
// The spine 04-B stands on: 0019's CHECK makes a floor-captured qty report
// without a `shift_instance_id` unrepresentable, so the shift-definition CRUD
// (endpoint 24) and the tables behind it ship FIRST, before the batch endpoint
// that needs them (04-B-2). `shift_instances` has no HTTP surface of its own in
// this sub-step — instances materialize lazily / by the shift tick and are
// served through 04-B-2's snapshot (e15) and 04-C's scoreboard (e17).

/** A display name in the platform locale set (spec 04 §2.9 `name_i18n`).
 * Same rule as module 05's `WasteLocaleName`, restated rather than imported so
 * the two modules' contracts stay independently evolvable: `en` REQUIRED,
 * every other locale optional — demanding all four produces FAKE translations
 * typed in to get past the form, which the product can then never tell from a
 * real one. */
export const ShiftLocaleName = z
  .object({
    ...localeKeys(z.string().min(1).max(120).optional()),
    en: z.string().min(1).max(120),
  })
  .openapi({ description: "Display name per locale; `en` required, others optional" });

/** `DAY`, `NIGHT` — UPPER_SNAKE, mirroring the DB CHECK. `GENERAL` is the
 * provisioned D-078 fallback and is refused on create (it already exists in
 * every facility) and on edit/deactivate (SHIFT_GENERAL_IMMUTABLE). */
export const ShiftCode = z
  .string()
  .regex(/^[A-Z][A-Z0-9_]{0,31}$/)
  .openapi({ example: "DAY", description: "Stable UPPER_SNAKE shift code, unique per facility" });

/** Facility-local wall time, `HH:MM` (spec 04 §2.9 `time` columns). `end_time`
 * <= `start_time` means the shift crosses midnight; `00:00`→`00:00` is the full
 * 24 h of the facility-local calendar date (the GENERAL window). */
export const ShiftTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
  .openapi({ example: "08:00", description: "Facility-local wall time, HH:MM" });

/** ISO weekday numbers 1 (Mon) … 7 (Sun), unique, never empty — a definition
 * active on no day is a window no instant can resolve into. */
export const ShiftDaysOfWeek = z
  .array(z.number().int().min(1).max(7))
  .min(1)
  .max(7)
  .refine((days) => new Set(days).size === days.length, { message: "weekdays must be unique" })
  .openapi({ example: [1, 2, 3, 4, 5, 6, 7], description: "ISO weekday numbers (1 = Monday)" });

export const ShiftDefinition = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    code: ShiftCode,
    name_i18n: ShiftLocaleName,
    start_time: ShiftTime,
    end_time: ShiftTime,
    days_of_week: ShiftDaysOfWeek,
    active: z.boolean(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("ShiftDefinition");

export const ShiftDefinitionResponse = z
  .object({ shift_definition: ShiftDefinition })
  .openapi("ShiftDefinitionResponse");

/** The full per-facility calendar is small (a factory runs 1–3 shifts), so the
 * list is envelope-without-cursor — same shape as the waste catalogs. */
export const ShiftDefinitionListResponse = z
  .object({ items: z.array(ShiftDefinition) })
  .openapi("ShiftDefinitionListResponse");

export const ShiftDefinitionCreateRequest = z
  .object({
    facility_id: Uuid,
    code: ShiftCode,
    name_i18n: ShiftLocaleName,
    start_time: ShiftTime,
    end_time: ShiftTime,
    days_of_week: ShiftDaysOfWeek.optional(),
  })
  .openapi("ShiftDefinitionCreateRequest");

/** PATCH: partial; `active: false` is the deactivate action (§5 row 24 has no
 * DELETE — closed instances and, from 04-B-2, qty reports reference the row). */
export const ShiftDefinitionUpdateRequest = z
  .object({
    name_i18n: ShiftLocaleName.optional(),
    start_time: ShiftTime.optional(),
    end_time: ShiftTime.optional(),
    days_of_week: ShiftDaysOfWeek.optional(),
    active: z.boolean().optional(),
  })
  .openapi("ShiftDefinitionUpdateRequest");

// --- downtime (spec 04 §2.6/§2.7, §5 e22/e23 — slice 04-C-1) ----------------

/** §2.6's closed `kind` vocabulary — mirrors the DB CHECK and the vertical
 * pack schema (`packages/verticals` DowntimeReason.kind). */
export const DowntimeReasonKind = z.enum([
  "machine_breakdown",
  "changeover",
  "material_wait",
  "no_operator",
  "quality_hold",
  "planned_maintenance",
  "power",
  "other",
]);

/** One §2.6 catalog row. `code` is stable UPPER_SNAKE (the pack seeds
 * `reason.die_change` → `DIE_CHANGE`, the same ref→code rule as the waste
 * catalogs); `active: false` is the deactivate state — logs reference a
 * reason forever, so there is no DELETE. */
export const DowntimeReason = z
  .object({
    id: Uuid,
    code: z.string(),
    name_i18n: ShiftLocaleName,
    kind: DowntimeReasonKind,
    /** §2.6: true for breakdown-class reasons — `production.downtime_logged`
     * carries it and module 08 opens a breakdown on it when 08 lands. */
    triggers_maintenance: z.boolean(),
    active: z.boolean(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("DowntimeReason");

export const DowntimeReasonResponse = z
  .object({ downtime_reason: DowntimeReason })
  .openapi("DowntimeReasonResponse");

/** A tenant's catalog is small (the cable pack ships 4) — envelope without a
 * cursor, the shift-definition shape. */
export const DowntimeReasonListResponse = z
  .object({ items: z.array(DowntimeReason) })
  .openapi("DowntimeReasonListResponse");

export const DowntimeReasonCode = z
  .string()
  .regex(/^[A-Z][A-Z0-9_]{0,63}$/)
  .openapi({
    example: "DIE_CHANGE",
    description: "Stable UPPER_SNAKE reason code, unique per tenant",
  });

export const DowntimeReasonCreateRequest = z
  .object({
    code: DowntimeReasonCode,
    name_i18n: ShiftLocaleName,
    kind: DowntimeReasonKind,
    triggers_maintenance: z.boolean().optional(),
  })
  .openapi("DowntimeReasonCreateRequest");

/** PATCH: partial; `code` is immutable (floor caches key on it and logs
 * reference the row); `active: false` is the deactivate action. */
export const DowntimeReasonUpdateRequest = z
  .object({
    name_i18n: ShiftLocaleName.optional(),
    kind: DowntimeReasonKind.optional(),
    triggers_maintenance: z.boolean().optional(),
    active: z.boolean().optional(),
  })
  .openapi("DowntimeReasonUpdateRequest");

/**
 * One §2.7 row as §5 e22 serves it. `machine_id` is declared and NULL
 * throughout P2 (D-073 — the work centre is the downtime grain; the column
 * fills when module 08 lands, an additive data change). `duration_minutes` is
 * the DB-GENERATED whole-minute interval, null while the row is open. No
 * money rides a downtime log, so §7's redaction set has nothing to strip.
 */
export const DowntimeLog = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    work_center_id: Uuid,
    machine_id: Uuid.nullable(),
    wo_operation_id: Uuid.nullable(),
    downtime_reason_id: Uuid,
    started_at: Timestamp,
    ended_at: Timestamp.nullable(),
    duration_minutes: z.number().int().nullable(),
    reported_by: Uuid,
    shift_instance_id: Uuid.nullable(),
    maintenance_breakdown_id: Uuid.nullable(),
    notes: z.string().nullable(),
    created_at: Timestamp,
  })
  .openapi("DowntimeLog");

export const DowntimeLogListResponse = z
  .object({ items: z.array(DowntimeLog), next_cursor: Cursor })
  .openapi("DowntimeLogListResponse");

/**
 * §5 e35 — end an OPEN stoppage from the office (D-299, owner ruling H). The
 * floor's `downtime_ended` renders only on a station that still shows a live
 * operation, so a row opened against a finished order — or on a station that
 * went quiet — had no ender anywhere, and an op-bound one held §3.1's close
 * shut. This is that ender.
 *
 * `note` is REQUIRED (trimmed; blank-after-trim is 422 `VALIDATION_FAILED`):
 * the office is overriding floor truth, and every override in this module
 * states its reason into the audit chain (§2.17, e25, e32).
 *
 * `ended_at` is OPTIONAL and means "when the machine actually came back".
 * Absent, the stoppage ends at the request's instant. The field exists because
 * the normal case is a forgotten end found hours or days later, and ending it
 * "now" would book every minute since as downtime on the scoreboard. A stated
 * instant must lie after the row's `started_at` and not after the server's
 * now — 422 `DOWNTIME_END_AT_INVALID` otherwise, with `bound`
 * (`not_after_start` | `in_future`) and the row's `started_at` in the problem.
 */
export const DowntimeLogEndRequest = z
  .object({
    note: z.string().min(1).max(2000),
    ended_at: isoDateTime({ offset: true }).optional().openapi({
      description:
        "When the stoppage actually ended (ISO-8601 with offset). Omit to end it now. Must be after the log's started_at and not later than now.",
    }),
  })
  .openapi("DowntimeLogEndRequest");

/** The ended row, and the work order whose §3.1 close it was holding — null
 * for station (op-less) downtime, which never blocks a close. A client that
 * came from a refused close uses it to offer the close again. */
export const DowntimeLogEndResponse = z
  .object({ downtime_log: DowntimeLog, work_order_id: Uuid.nullable() })
  .openapi("DowntimeLogEndResponse");

// --- calibration (spec 04 §2.8/§3.8, §5 e20/e21 — slice 04-C-2) -------------
//
// `CalibrationMeasurementMethod` and `CalibrationCaptureShape` were hoisted to
// the closed-vocabularies block above on 2026-08-05 (D-084) — e34's request
// needs them, and a request schema declared before them cannot reference them.

/**
 * One §2.8 row as §5 e21 serves it. Work-centre-keyed (D-073): `machine_id` is
 * declared and NULL throughout P2, filled additively when module 08 lands.
 * `variance_qty`/`variance_pct` are the DB-GENERATED derivations (measured −
 * claimed; never entered), served as exact decimal text like every quantity
 * (D-041). No money rides a calibration record — the §3.6 cost entry lives on
 * the WO ledger behind `production.view_costs`.
 */
export const CalibrationRecord = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    work_center_id: Uuid,
    machine_id: Uuid.nullable(),
    work_order_id: Uuid,
    wo_operation_id: Uuid,
    /** Module 06 barrel/bobbin this batch landed on — resolved server-side
     * from the §5.1 scan; null when the tap carried no scan. */
    output_stock_unit_id: Uuid.nullable(),
    batch_label: z.string(),
    machine_claimed_qty: ProductionQty,
    /** The ONLY number inventory, costing and genealogy ever use (D-025). */
    measured_qty: ProductionQty,
    uom: z.string(),
    /** Signed exact decimal: measured − claimed. */
    variance_qty: z.string(),
    /** Signed exact decimal percentage (4 dp), e.g. "-8.3333". */
    variance_pct: z.string().nullable(),
    measurement_method: CalibrationMeasurementMethod,
    measured_by: Uuid,
    measured_at: Timestamp,
    /**
     * Which capture path measured this barrel (§2.8, added 2026-08-05 —
     * D-084): `floor` = a device `calibration_recorded` tap at the station;
     * `office` = the §3.11 paper-traveler capture on e34. It rides the wire
     * for the same reason §2.4's does on a quantity report — "the tablet said
     * 5980" and "someone typed 5980" are never the same fact, and a surface
     * that shows barrels must be able to say which it is showing.
     *
     * Both paths COUNT toward §3.8 drift, deliberately: drift is a
     * MEASUREMENT surface, not the §3.7 ATTRIBUTION surface that office rows
     * are invisible to.
     */
    capture_source: OperationCaptureSource,
    created_at: Timestamp,
  })
  .openapi("CalibrationRecord");

export const CalibrationRecordListResponse = z
  .object({ items: z.array(CalibrationRecord), next_cursor: Cursor })
  .openapi("CalibrationRecordListResponse");

/** One drift-chart bucket (§5 e20): day/week aggregates of `variance_pct`.
 * Percentages are diagnostics — never money, never stock (D-025) — so they
 * ride as JSON numbers; `date` is the bucket's first day, facility-agnostic
 * UTC date of `measured_at`. */
export const CalibrationDriftBucket = z
  .object({
    date: z.string().openapi({ format: "date" }),
    avg_variance_pct: z.number(),
    min_variance_pct: z.number(),
    max_variance_pct: z.number(),
    stddev_pct: z.number(),
    barrel_count: z.number().int(),
  })
  .openapi("CalibrationDriftBucket");

/**
 * The §3.8 trailing-window status e20 computes AT READ from the same rows the
 * chart plots — deterministic from data, so the chart's breach flag can never
 * disagree with the cron's classification (both call the same domain-kernel
 * decision). `classification` null = below both thresholds.
 */
export const CalibrationDriftStatus = z
  .object({
    window_days: z.number().int(),
    mean_variance_pct: z.number(),
    stddev_pct: z.number(),
    barrel_count: z.number().int(),
    classification: z.enum(["calibration_bias", "meter_unreliable"]).nullable(),
    /** The D-083 thresholds the status was judged against (spec defaults 3/5;
     * facility-configurability is STATED PENDING — spec 04 §12/§2.8). */
    mean_threshold_pct: z.number(),
    stddev_threshold_pct: z.number(),
    /** The D-083 barrel floor: below this many records in the window the
     * kernel does NOT classify, so `classification: null` means UNEVALUATED
     * here and "within thresholds" above it. The server carries the number
     * because the client must be able to tell those two apart — rendering
     * "within thresholds" over four bad barrels is a false statement about
     * the machine, and a hardcoded client copy would silently diverge the
     * day the floor becomes facility-configurable. */
    min_barrels: z.number().int(),
    /**
     * §3.8's money impact over the same trailing window: Σ |amount_minor| of
     * the window's `calibration_variance` cost entries — the ledger the
     * calibration engine wrote, never a re-derivation, and the IDENTICAL sum
     * `production.calibration_drift_detected` carries (both read
     * `CalibrationRepo.costImpactSince`). A credit is impact too (§9.9), which
     * is why the sum is over absolute values.
     *
     * ADDED 2026-08-05 while writing M04-S04: §10.4's amendment requires the
     * S6 chart to show "the barrel count AND the money impact in KZT minor
     * units ON THE SAME SCREEN", and neither this response nor the screen
     * carried a money field — the walkthrough could not have made the clause
     * without asserting it somewhere else, which would have been a different
     * claim. Additive, so no `oasdiff` breaking change.
     *
     * OPTIONAL because §7 lists `est_cost_impact_minor` in the redaction set:
     * `maintenance` holds `production.telemetry.read` and NOT
     * `production.view_costs`, so it reads this chart with the money key
     * ABSENT — never null (the same key-absence rule the work-order payloads
     * follow, and the reason the field is optional rather than nullable).
     */
    est_cost_impact_minor: MinorMoney.optional(),
    /** The currency those entries are denominated in (the work order's, per
     * §9.13 fixed at creation); the work centre's when the window posted no
     * variance entry at all. Never a hardcoded KZT on the client. Absent
     * exactly when the amount is — a currency with no amount says nothing and
     * would only invite a client to render "— KZT". */
    currency: z.string().length(3).optional(),
  })
  .openapi("CalibrationDriftStatus");

export const CalibrationDriftResponse = z
  .object({
    work_center_id: Uuid,
    bucket: z.enum(["day", "week"]),
    buckets: z.array(CalibrationDriftBucket),
    /** Newest-first tail of the window — the S6 "recent barrels" table. */
    records_recent: z.array(CalibrationRecord),
    drift: CalibrationDriftStatus,
  })
  .openapi("CalibrationDriftResponse");

// --- scoreboard + piece rate (spec 04 §2.10/§2.13, §5 e16/e17/e27 — 04-C-4) --

// The scoreboard's quantities ride `ProductionSignedQty` (declared above with
// the material-issue reversal it was minted for), and the SIGN is reachable
// here for a different reason worth stating: a §2.17 compensating correction is
// attributed to the ORIGINAL event's shift instance, so an operation reported
// across two shifts can legally take a correction larger than either shift's
// own share. Rendering that as `0` would be the API telling a supervisor a
// number the database does not hold.

/**
 * One §2.10 row — the (shift, work centre) cell §3.7's whole anti-gaming
 * mechanic is about.
 *
 * **NO MONEY, and not because it was redacted.** §2.10 has no cost columns at
 * all: the scoreboard hangs on a wall in a corridor, and a column that must be
 * hidden from half its audience does not belong on a surface whose audience is
 * "whoever walks past". `first_pass_yield_pct` is the DB-GENERATED derivation
 * of the three quantities beside it (D-087 ruling 3) — null when the
 * denominator is 0, which is not the same claim as 0%.
 *
 * `defects_handed_off` / `defects_flagged` are DECLARED and honestly 0 in this
 * release: their writer is 04-C-5's `defect_handoff_flagged` applier. The S5
 * screen says so rather than drawing an unexplained empty column.
 */
export const ShiftScoreboardRow = z
  .object({
    shift_instance_id: Uuid,
    shift_definition_id: Uuid,
    /** The shift's human code (`DAY`, `NIGHT`) — the row's own identity, not a
     * convenience. A date with two shifts returns TWO rows per work centre, and
     * a board that could only name the work centre showed one station twice
     * with different numbers and nothing saying which shift they belonged to.
     * §3.7's whole claim is that a number belongs to a SPECIFIC shift.
     *
     * It rides the ROW rather than being joined client-side from e24 because
     * e24 is gated on `production.read` and this endpoint on
     * `production.scoreboard.read`: every system role holds both, but D-074's
     * grammar admits a custom role holding only the latter, and that role would
     * have got a board whose rows could not name themselves. */
    shift_definition_code: ShiftCode,
    shift_date: BusinessDate,
    facility_id: Uuid,
    work_center_id: Uuid,
    qty_good: ProductionSignedQty,
    qty_scrap: ProductionSignedQty,
    qty_rework: ProductionSignedQty,
    first_pass_yield_pct: ProductionPct.nullable(),
    /** §9.11: minutes INSIDE this window only — a downtime row spanning a
     * shift boundary is split between two rows and counted once in each. */
    downtime_minutes: z.number().int(),
    defects_handed_off: ProductionQty,
    defects_flagged: ProductionQty,
    /** Distinct workers with a FLOOR qty report on this cell. Office (§3.11)
     * rows carry no shift and cannot contribute — by construction, not by a
     * filter. */
    worker_count: z.number().int(),
    /** True once the shift-close job recomputed this row from source (§3.7).
     * A live shift's rows are the incremental ones and say so. */
    finalized: z.boolean(),
    updated_at: Timestamp,
  })
  .openapi("ShiftScoreboardRow");

/** Per-facility totals for the §4 S5 header and the S12 wall board. The yield
 * is recomputed over the SUMS, not averaged over the rows — a work centre that
 * made 10 m and one that made 10 km do not get an equal vote. */
export const ShiftScoreboardFacilityTotal = z
  .object({
    facility_id: Uuid,
    qty_good: ProductionSignedQty,
    qty_scrap: ProductionSignedQty,
    qty_rework: ProductionSignedQty,
    first_pass_yield_pct: ProductionPct.nullable(),
    downtime_minutes: z.number().int(),
    worker_count: z.number().int(),
  })
  .openapi("ShiftScoreboardFacilityTotal");

export const ShiftScoreboardResponse = z
  .object({
    shift_date: BusinessDate,
    rows: z.array(ShiftScoreboardRow),
    facility_totals: z.array(ShiftScoreboardFacilityTotal),
    /** Server time at which these rows were read — the S12 wall board stamps
     * its cached render with it (§9.16), so a tablet that lost the network
     * shows how old its number is instead of a confident wrong one. */
    as_of: Timestamp,
  })
  .openapi("ShiftScoreboardResponse");

/**
 * §5 e16 — the worker's OWN tally for the current shift. Self-only by
 * construction: the server aggregates the SESSION principal's rows and takes no
 * `worker_id` at all, so there is no parameter to tamper with (D-079's identity
 * rule, applied to a read).
 *
 * NO MONEY. §4's S11 is a phone screen a worker glances at mid-shift; piece
 * QUANTITY is what it can honestly show, because §12 Q1's rate table does not
 * exist and a rate this module invented would be a number somebody would
 * expect on a payslip.
 */
export const MyShiftSummaryResponse = z
  .object({
    /** Null when no shift instance covers `now` at any facility this session
     * can see — a worker signed in outside every window. The screen says so
     * instead of showing four zeros that look like a bad shift. */
    shift_instance_id: Uuid.nullable(),
    shift_definition_code: z.string().nullable(),
    shift_date: BusinessDate.nullable(),
    shift_started_at: Timestamp.nullable(),
    shift_ended_at: Timestamp.nullable(),
    qty_good: ProductionQty,
    qty_scrap: ProductionQty,
    ops_completed: z.number().int(),
    /** Σ `piece_rate_entries.qty` for this worker in this shift — scrap never
     * earns it (§2.13). */
    piece_qty: ProductionQty,
    as_of: Timestamp,
  })
  .openapi("MyShiftSummaryResponse");

/**
 * One §2.13 row as §5 e27 serves it.
 *
 * `rate_minor` / `amount_minor` are OPTIONAL IN THE CONTRACT because §7's
 * redaction shape for this module is ABSENCE — the key is not present at all
 * for a principal outside §2.13's visibility list (D-074 pt 3), which is a
 * stronger and more checkable assertion than a null beside a marker. When they
 * ARE present they are still null in this release: nothing prices, because §12
 * Q1 is open.
 */
export const PieceRateEntry = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    worker_id: Uuid,
    wo_operation_id: Uuid,
    qty_report_id: Uuid,
    shift_instance_id: Uuid,
    shift_date: BusinessDate,
    reported_at: Timestamp,
    qty: ProductionQty,
    rate_minor: MinorMoney.nullable().optional(),
    amount_minor: MinorMoney.nullable().optional(),
    currency: z.string().length(3),
    status: z.enum(["captured", "priced", "settled"]),
    created_at: Timestamp,
  })
  .openapi("PieceRateEntry");

export const PieceRateEntryListResponse = z
  .object({ items: z.array(PieceRateEntry), next_cursor: Cursor })
  .openapi("PieceRateEntryListResponse");

// ===========================================================================
// §2.11 defect hand-offs (§5 e18/e19 — slice 04-C-5)
// ===========================================================================
//
// THE COMMERCIAL POINT OF THIS MODULE, on the wire. A hand-off row is one
// shift's accusation about another's work, and everything in the DTO exists to
// make that accusation checkable: WHO produced it (a shift always; a worker
// only when the qty-report grain identified exactly one), WHO found it, HOW
// MUCH, and — once someone decides — what was done about it.
//
// NO MONEY, and none to redact: a hand-off carries quantities and reason codes
// only. The scrap it may become is costed on the WORK ORDER's ledger through
// the §3.6 entries the disposition posts, behind `production.view_costs`, on a
// different surface.

export const DefectHandoffDisposition = z.enum(["pending", "rework", "scrap", "accepted_as_is"]);

/** One §2.11 row as §5 e18/e19 serve it. */
export const DefectHandoff = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    work_order_id: Uuid,
    /** Where the defect was DISCOVERED — the flagger's station. */
    wo_operation_id: Uuid,
    /** Where it was PRODUCED. Always present: the server resolves it from the
     * payload when the flagger knew, else the discovery op (§2.11's "defaults
     * to same op"), because the retro-attribution keys the producing shift's
     * scoreboard cell on THIS operation's work centre. */
    produced_wo_operation_id: Uuid,
    /** §3.3 step 10's attribution, resolved SERVER-SIDE by walking the
     * producing op's qty reports backwards from the flag. Never on the request
     * wire — a device that could name the shift it blamed would be the gaming
     * vector this table closes. */
    produced_shift_instance_id: Uuid,
    produced_shift_definition_code: ShiftCode,
    produced_shift_date: BusinessDate,
    /** Pinned only when the covering qty window had exactly ONE reporter; null
     * means shift-level attribution, which is the honest answer for a crew. */
    produced_by_worker_id: Uuid.nullable(),
    flagged_shift_instance_id: Uuid,
    flagged_shift_definition_code: ShiftCode,
    flagged_shift_date: BusinessDate,
    flagged_by_worker_id: Uuid,
    qty_defective: ProductionQty,
    /** Module 05 `waste_cause_codes.code` — service-validated, never an FK. */
    scrap_reason_code: ScrapReasonCode,
    disposition: DefectHandoffDisposition,
    disposition_by: Uuid.nullable(),
    disposition_at: Timestamp.nullable(),
    disposition_note: z.string().nullable(),
    /** Ids of `production_evidence` files uploaded BEFORE the flag (§3.4) —
     * the office resolves each through `GET /v1/files/{id}`. */
    photo_file_ids: z.array(Uuid),
    /** The FLAGGER's note; the disposer's reason is `disposition_note`. */
    notes: z.string().nullable(),
    source_event_id: Uuid,
    created_at: Timestamp,
    updated_at: Timestamp,
    /** The hand-off's work order's §3.1 status, as of the read (D-299 ruling
     * 8). A `rework` disposition re-opens a step only on a `released` or
     * `in_progress` order — e19 answers 409 `REWORK_BLOCKED_WO_NOT_LIVE`
     * otherwise — so the S5 ledger reads this to render Rework disabled, with
     * a sentence naming what remains, rather than offer a control the server
     * refuses. Additive: a response property, never a request field. */
    work_order_status: WorkOrderStatus,
  })
  .openapi("DefectHandoff");

export const DefectHandoffListResponse = z
  .object({ items: z.array(DefectHandoff), next_cursor: Cursor })
  .openapi("DefectHandoffListResponse");

/**
 * §5 e19. `pending` is not an accepted value: this endpoint DECIDES, and
 * "decide that it is undecided" is not a decision — a row that should not have
 * been flagged is `accepted_as_is` with a note saying so, which leaves a trail.
 *
 * `note` is REQUIRED for `scrap`, service-enforced: scrapping retro-attributes
 * the quantity to the PRODUCING shift's `qty_scrap` and therefore to its FPY,
 * and a number that moves onto somebody else's scoreboard with no stated reason
 * is the shape §2.17 refuses for discarded floor truth.
 */
export const DefectHandoffDispositionRequest = z
  .object({
    disposition: z.enum(["rework", "scrap", "accepted_as_is"]),
    note: z.string().min(1).max(2000).optional(),
  })
  .openapi("DefectHandoffDispositionRequest");

export const DefectHandoffResponse = z
  .object({ defect_handoff: DefectHandoff })
  .openapi("DefectHandoffResponse");

/** §5 e32 — the MANUAL `qc_hold` release. The note is required and lands in
 * the hash-chained audit row: clearing a hold a failed inspection put on a step
 * is a person overriding a quality decision, and the chain is where that
 * override lives. */
export const QcHoldReleaseRequest = z
  .object({ note: z.string().min(1).max(2000) })
  .openapi("QcHoldReleaseRequest");

// --- §5 query-filter runtime schemas (spec 04 §9.18 — fixed 2026-08-08) -----

/**
 * Empty string means ABSENT for every optional §5 query filter — the house
 * `emptyToUndefined` rule every production list controller already applied
 * before validation existed. Baked into the schema so the contract and the
 * runtime can never disagree about what `?facility_id=` means.
 */
const emptyIsAbsent = (value: unknown): unknown => (value === "" ? undefined : value);

/**
 * One §5 uuid QUERY FILTER, enforced at the controller with `ZodPipe` (spec 04
 * §9.18). Every production repo binds these filters into a `$n::uuid` cast (or
 * an `= $n` against a uuid column, which Postgres types identically), so a
 * malformed value that reaches SQL surfaces 22P02 as a 500 `INTERNAL`. This
 * schema is the CONTRACT's own `format: uuid` declaration made load-bearing:
 * absent/empty = no filter, anything else must parse as a uuid or the request
 * answers the documented 400 `VALIDATION_FAILED` — and the §9.14 no-oracle 404
 * for a WELL-FORMED foreign/nonexistent id is unchanged behind it.
 */
export const UuidQueryFilter = z.preprocess(emptyIsAbsent, Uuid.optional());

/**
 * A §5 BUSINESS-DATE query filter (`YYYY-MM-DD` — e2's `due_before`/
 * `due_after`, e20's `from`/`to`): the same §9.18 class through a `::date`
 * cast (or a JS `new Date` the driver then refuses), closed the same way.
 */
export const DateQueryFilter = z.preprocess(emptyIsAbsent, z.iso.date().optional());

/**
 * A §5 INSTANT query filter (ISO 8601 with offset — e21's `from`/`to`),
 * mirroring the register's `isoDateTime({ offset: true })`.
 */
export const DatetimeQueryFilter = z.preprocess(
  emptyIsAbsent,
  isoDateTime({ offset: true }).optional(),
);

/**
 * Empty-is-absent for an ARRAY-SHAPED filter (§9.18 enum extension,
 * 2026-08-08): a single bare value is the one-element array (the qs shape
 * Express hands a controller), empty members are dropped, and an empty result
 * is no filter at all.
 */
export const arrayEmptyIsAbsent = (value: unknown): unknown => {
  if (value === undefined) return undefined;
  const raw = Array.isArray(value) ? value : [value];
  const kept = raw.filter((v) => v !== "");
  return kept.length === 0 ? undefined : kept;
};

/**
 * §5 e2's `status[]` QUERY FILTER (§9.18 extended to the DECLARED-ENUM filters
 * 2026-08-08, with e10's `event_type[]` and e22's `reason_kind`): the OpenAPI
 * document has declared the six-status enum since 04-A while the runtime
 * handed any string to `status = ANY($n)` and answered a silent 200-empty —
 * the contract and the wire disagreeing about what a legal filter is, the
 * exact class §9.18 closed for uuid/date filters. An unknown member answers
 * the documented 400 `VALIDATION_FAILED`; absent/empty = no filter.
 */
export const WorkOrderStatusQueryFilter = z.preprocess(
  arrayEmptyIsAbsent,
  z.array(WorkOrderStatus).optional(),
);

/**
 * §5 e22's `reason_kind` QUERY FILTER — the same §9.18 enum extension: the
 * register has declared `DowntimeReasonKind` since 04-C-1 while the runtime
 * passed the raw string into `dr.kind = $n`.
 */
export const DowntimeReasonKindQueryFilter = z.preprocess(
  emptyIsAbsent,
  DowntimeReasonKind.optional(),
);

// --- problem codes (spec 04 §5/§9) -----------------------------------------

/**
 * The RFC 9457 `code`s slice 04-A's endpoints can raise.
 *
 * Gated both ways once module 04's block lands in
 * `apps/api/test/unit/module-catalog-parity.test.ts`: a code raised in a
 * handler that is not here fails, and a code here that no handler can raise
 * fails. The list is therefore kept to the refusals the 04-A surface actually
 * has — the floor conflict reasons (§2.17) are not problem codes at all (a
 * conflicted floor event answers 200 with a per-event status) and the 04-B/04-C
 * refusals arrive with their slices.
 */
export const productionProblemCodes = [
  "WORK_ORDER_NOT_FOUND",
  "WORK_ORDER_OPERATION_NOT_FOUND",
  "MATERIAL_ISSUE_NOT_FOUND",
  // --- cross-module references a work order pins (spec 04 §2.1, §3.9) ------
  // A work order names a product, a facility, a BOM version, a routing version
  // and — through its operations — work centres, none of which are this
  // module's rows. Every one of them is resolved through the owning module's
  // service API and every one can MISS, so the misses are catalogued here
  // rather than escaping as a bare platform `NOT_FOUND`: a planner told "not
  // found" cannot tell a mistyped product from a facility they cannot reach.
  // Cross-tenant ids answer identically to absent ones (§9.14 — no existence
  // oracle), which is what the RLS floor gives for free.
  "PRODUCT_NOT_FOUND",
  "FACILITY_NOT_FOUND",
  "WORK_CENTER_NOT_FOUND",
  "BOM_NOT_FOUND",
  "ROUTING_NOT_FOUND",
  /** No BOM version is effective for this product at this facility today
   * (spec 01 §3.4 — module 03 turns this into a planning exception). */
  "NO_EFFECTIVE_BOM",
  "NO_EFFECTIVE_ROUTING",
  /** The §3.1 / §3.2 state machines: no other transitions exist. */
  "WO_INVALID_TRANSITION",
  /** A draft-only edit attempted on a released-or-later WO (§5 row 8). */
  "WO_NOT_EDITABLE",
  /** Any mutation on a closed WO — close immutability (§3.1, §9.4). */
  "WO_CLOSED_IMMUTABLE",
  /** Release found shortages and the caller did not acknowledge them (§4 S3). */
  "WO_RELEASE_SHORTAGES_UNACKNOWLEDGED",
  /** Complete guard: an operation is neither completed nor skipped (§3.1). */
  "WO_COMPLETE_BLOCKED",
  /** Close guard: open downtime, pending hand-offs, or open sync conflicts
   * referencing this WO (§3.1). */
  "WO_CLOSE_BLOCKED",
  /** Cancel guard: material is still issued and unreturned, or produced good
   * quantity is unreceived and unscrapped (§3.1, §9.12). */
  "WO_CANCEL_DISPOSITION_REQUIRED",
  /** The pinned BOM's finished + co-product apportionment does not sum to
   * 100.000, so the §2.18 pool cannot be divided (§2.18). */
  "WO_OUTPUT_APPORTIONMENT_INVALID",
  // --- the unit boundary (§3.1, D-221) -------------------------------------
  // Module 04 states a plan in the BOM line's unit and module 06 answers in the
  // component's base unit, so every comparison between them converts first.
  // Both refusals below are module 06's own codes for the same two failures
  // (spec 06 §2.10/§9.6), catalogued here because module 04 now raises them at
  // its own doors — a caller must read one meaning for one code whichever
  // module answered.
  /** No product-specific or generic conversion connects a stated unit to the
   * product's base — for a BOM line this means an edge was deleted after
   * module 01's activation gate passed it, and for a work order it means the
   * order was keyed in a unit module 06 cannot receive output in. */
  "UOM_NOT_CONVERTIBLE",
  /** A conversion could not be applied without losing significance — the
   * kernel refuses rather than rounding a physical quantity silently. */
  "MEASURE_PRECISION_LOSS",
  /** An operation result or skip against a step that cannot take one (§3.2). */
  "OPERATION_NOT_ACTIONABLE",
  /** e34's stated `ended_at` is later than the server's now (422, D-299 owner
   * ruling V3): a step cannot end at an instant that has not happened. The
   * predicate is §5 e35's `in_future` bound, with no forward tolerance, and
   * the problem carries `bound: in_future` in e35's shape. An earlier
   * `ended_at` is accepted, because the office keys paper travelers after the
   * fact. Its own code rather than e35's, whose title speaks of a stoppage. */
  "OPERATION_END_AT_INVALID",
  /** e34's stated `started_at` is later than the server's now (422, D-299
   * owner ruling W2): a step cannot start at an instant that has not happened
   * either. The same predicate and the same `bound: in_future` shape as
   * `OPERATION_END_AT_INVALID`, refused at the same point, after every 409 and
   * before the first write. Its own code, because that code's title speaks of
   * the step's end. */
  "OPERATION_START_AT_INVALID",
  /** `qty_scrap > 0` with no cause code (§2.4, §3.11). */
  "SCRAP_REASON_REQUIRED",
  /** The cause code is not in this tenant's Module 05 catalog (§2.4). */
  "SCRAP_REASON_UNKNOWN",
  /** A return-to-stock exceeds what remains issued — net of returns already
   * booked (§5 row 30, and D-081's floor allocation guard). */
  "MATERIAL_RETURN_EXCEEDS_ISSUED",
  // `MATERIAL_RETURN_UNSUPPORTED` RETIRED 2026-08-05 (slice 04-B-4). Its own
  // comment predicted this: "the ruling belongs in spec 06 §2.4 and this code
  // retires with it". D-076 specified the `material_return` movement type,
  // D-081 realized both halves, and e30's negative branch now POSTS instead of
  // refusing — the D-076-predicted inversion, not a weakening.
  /** The pinned BOM prices a by-product in a currency other than the work
   * order's own (§9.13 fixes the WO currency at creation and this module holds
   * no FX rate — crediting a KZT pool with a USD number is a money error no
   * ledger could unwind). */
  "WO_OUTPUT_CURRENCY_MISMATCH",
  /** The traveler PDF (§5 row 29) contains a character no bundled font can
   * draw. The same refusal module 02 gives for a label sheet, for the same
   * reason: printing tofu is the dishonest degradation. */
  "TRAVELER_GLYPH_COVERAGE",
  // --- calibration (§2.8/§3.2, §5 e20/e21/e34 — slice 04-C-2) ---------------
  /** The §3.2 guard on the OFFICE path (e34 `complete: true`): the step
   * `requires_measured_capture` and no calibration record exists for the op.
   * The floor path answers the same guard as an `invalid_transition` conflict
   * (§5 r13's refusal shape), never a problem envelope. */
  "MEASURED_CAPTURE_REQUIRED",
  // --- serial/lot capture (§2.14/§3.2, §5 e34 — slice 04-C-3) --------------
  /** The §3.2 guard on the OFFICE path (e34 `complete: true`): the step
   * `requires_serial_capture` and no PRODUCED §2.14 capture exists for the op,
   * nor does this call supply one. The floor path answers the same guard as an
   * `invalid_transition` conflict, never a problem envelope — the same split
   * `MEASURED_CAPTURE_REQUIRED` makes, for the same reason (a device batch has
   * no human at the far end; a traveler entry does). */
  "SERIAL_CAPTURE_REQUIRED",
  // --- downtime catalog (§2.6, §5 e23 — slice 04-C-1) -----------------------
  "DOWNTIME_REASON_NOT_FOUND",
  /** UNIQUE (tenant_id, code) — 409, the SHIFT_CODE_TAKEN shape. */
  "DOWNTIME_REASON_CODE_TAKEN",
  // --- the office end of a stoppage (§2.7, §5 e35 — D-299 ruling H) --------
  /** e35 on an id that does not resolve: another tenant's row, a row in a
   * facility outside the caller's grant, and an id that never existed answer
   * byte-identically (§9.14 — the RLS floor hides all three). */
  "DOWNTIME_LOG_NOT_FOUND",
  /** e35 on a row whose `ended_at` is already set (409). Ending is ONCE: the
   * first end published its interval and moved the scoreboard, and a second
   * would publish a different interval for the same stoppage. The problem
   * carries `ended_at`. Also the answer when the office loses a race to the
   * floor's own `downtime_ended` — both serialize on the row lock. */
  "DOWNTIME_ALREADY_ENDED",
  /** e35's stated `ended_at` is not an instant the stoppage could have ended
   * at (422): not after the row's `started_at`, later than the server's now,
   * or more than 24 hours ago. `bound` names which (`not_after_start` |
   * `in_future` | `before_window`) and `started_at` rides along, so a client
   * can say it in the reader's language. e25 answers it too, for S7's
   * re-apply of a rejected `downtime_ended` (D-299 owner ruling Q2). */
  "DOWNTIME_END_AT_INVALID",
  /** e25's re-apply of a rejected tap that STARTS a stoppage — a
   * `downtime_started`, or an `op_paused` carrying a downtime reason — whose
   * tablet instant is more than 24 hours back (422, D-299 owner ruling S1).
   * The start's sibling of `DOWNTIME_END_AT_INVALID`, with its own title so a
   * client never tells the reader it was an end; `bound: before_window` and
   * `earliest_started_at` ride along. */
  "DOWNTIME_START_AT_INVALID",
  /** e25's re-apply of a rejected tap that starts or stops the crew's labor
   * timers — an `op_started`, `op_resumed`, `op_paused` or `op_completed` —
   * whose tablet instant is more than 24 hours back (422, D-299 owner ruling
   * U1: the one office window, [now − 24 h, now], bounds every trusted re-apply
   * that opens or closes an interval). The labor sibling of the two stoppage
   * codes: when the same tap also opens or ends a stoppage, that stoppage's
   * code answers instead; this one answers when neither reads true.
   * `bound: before_window` and `earliest_at` ride along. */
  "LABOR_AT_INVALID",
  // --- shift calendar (§2.9, §5 row 24 — slice 04-B-1) ----------------------
  "SHIFT_DEFINITION_NOT_FOUND",
  /** UNIQUE (tenant_id, facility_id, code) — 409, the FACILITY_CODE_TAKEN
   * shape. */
  "SHIFT_CODE_TAKEN",
  /** The candidate window intersects an ACTIVE non-GENERAL window of the same
   * facility on a shared weekday (half-open [start, end), cross-midnight
   * expanded). Refusing overlap is what makes shift resolution a TOTAL
   * function: at most one active non-GENERAL window contains any instant, and
   * the provisioned GENERAL (D-078) covers the rest. */
  "SHIFT_OVERLAP",
  /** The provisioned GENERAL row (D-078) is the fallback's floor: it cannot be
   * edited, deactivated, or re-created. */
  "SHIFT_GENERAL_IMMUTABLE",
  // --- floor spine (§2.3/§2.12/§2.16/§2.17, §5 rows 13/25/26/31 — 04-B-2) ---
  // NOTE: the §2.17 conflict REASONS are deliberately NOT problem codes — a
  // conflicted floor event answers HTTP 200 with a per-event status (§5 r13),
  // never a problem envelope. The codes below are the refusals of the OFFICE
  // surfaces around the queue.
  "FLOOR_CONFLICT_NOT_FOUND",
  /** Resolution is once: a resolved row refuses a second action (§2.17 — the
   * original decision stands; a wrong one is corrected by a further
   * compensating event, never by re-resolving). */
  "FLOOR_CONFLICT_ALREADY_RESOLVED",
  /** The action is illegal for the row's `kind` (§2.17): a rejection cannot be
   * acknowledged, a review_flag cannot be discarded. */
  "FLOOR_CONFLICT_ACTION_INVALID",
  /** `apply_with_edit`'s corrected payload cannot compose a correction (422):
   * malformed §5.1 shape, a zero delta, or a delta that would drive the
   * operation's quantity below zero (§2.17). */
  "FLOOR_CONFLICT_CORRECTION_INVALID",
  /** `apply_with_edit`'s corrected event was refused by the apply pipeline
   * (409) — e.g. the op completed since. The row stays OPEN for a different
   * decision (§9.4: resolution reopens nothing). */
  "FLOOR_CONFLICT_RESOLUTION_REJECTED",
  "FLOOR_DEVICE_NOT_FOUND",
  /** UNIQUE (tenant_id, device_code) — 409 (§2.16). */
  "FLOOR_DEVICE_CODE_TAKEN",
  /** e31: `ended_at` must be strictly after `started_at` — a manual labor
   * entry is always a closed, positive interval. */
  "LABOR_RANGE_INVALID",
  /** e31's `worker_id` does not resolve in this tenant. §9.14: a foreign
   * worker id answers byte-identically to a nonexistent one. */
  "WORKER_NOT_FOUND",
  // --- scoreboard + piece rate (§2.10/§2.13, §5 e16/e17/e27 — 04-C-4) -------
  /** §9.17: a `settled` piece-rate entry is immutable, so a §2.17 correction
   * over the qty report behind it is refused (409) rather than silently
   * re-stating a quantity payroll has already paid. Unreachable in this
   * release — nothing prices, so nothing settles — and enforced anyway, so the
   * module-11 pricing job inherits the rule instead of discovering it. */
  "PIECE_RATE_ENTRY_SETTLED",
  /** e17 without a resolvable shift date: `shift_date` is required and is a
   * calendar date (§9.7 — the date the shift STARTS, which for a night shift
   * is not the date its events happened on). */
  "SCOREBOARD_SHIFT_DATE_INVALID",
  // --- defect hand-offs + qc hold (§2.11/§2.2, §5 e18/e19/e32 — 04-C-5) ----
  "DEFECT_HANDOFF_NOT_FOUND",
  /** e19 on a row that already carries a disposition. The decision is ONE-SHOT
   * (§2.11's grain CHECK names who and when): it moved numbers between two
   * shifts' scoreboards and posted a compensating event, and re-deciding would
   * need a second compensation nobody asked for. A wrong disposition is
   * corrected the way §2.17 corrects wrong floor truth — by a further stated
   * act, never by overwriting the first one. */
  "DEFECT_HANDOFF_ALREADY_DISPOSED",
  /** e19 `scrap` with no note (422). Scrapping moves the quantity onto the
   * PRODUCING shift's `qty_scrap` and its FPY; an unexplained number landing on
   * another shift's board is exactly what §2.17 refuses for discarded floor
   * truth. */
  "DEFECT_HANDOFF_NOTE_REQUIRED",
  /** e19 `rework` whose PRODUCING operation's work order is no longer
   * `released`/`in_progress` (409, D-299 ruling 3). A re-open there would put
   * a step back to `in_progress` on an order whose output is already receipted
   * as lots, whose station queue no longer lists it and whose close would then
   * freeze it unworkable. The problem names the order's status and `available_dispositions`,
   * derived from the real guards: `accepted_as_is` always (so §3.1's close
   * guard is never stuck), `scrap` only when the C6 fence would admit it (no
   * live QC scrap on the producing operation — D-299 owner ruling F2); the
   * re-do itself is new work against the receipted lots, or a quarantine
   * (module 07). `closed` is answered earlier as `WO_CLOSED_IMMUTABLE`, for
   * every disposition. */
  "REWORK_BLOCKED_WO_NOT_LIVE",
  /** e32 on an operation whose `qc_hold` is already false — there is nothing to
   * release, and answering 200 would write an audit row claiming an override
   * that did not happen. */
  "QC_HOLD_NOT_HELD",
  /**
   * e34/e9 against an operation that IS on quality hold — the office half of
   * §3.2's guard, added 2026-08-17 (D-124).
   *
   * The hold fences the OPERATION, not the transport. §2.2 phrased its refused
   * set as four FLOOR EVENT kinds because, when it was written, module 07 did
   * not exist and the floor was the only path that could reach a held step. It
   * does now: 07-A's `QC_GATE_PORT` sets the flag in the completion
   * transaction, and D-088 ruling 9 states the invariant as "every tap on it is
   * refused `qc_hold` until §5 e32 clears it" — "a re-opened step nobody can
   * work yet". That was false on one side. A planner at a desk could record a
   * result through e34 and complete the very step quality had failed, while the
   * operator standing at the machine was refused, so the hold held half the
   * product.
   *
   * The refused set stays CLOSED and mirrors the floor's exactly: reporting
   * quantity and completing are refused; a material RETURN and a downtime
   * record stay legal, because a held station is a station standing still and
   * refusing those would make the hold erase the very minutes it caused and
   * strand the material issued to the step.
   */
  "OPERATION_ON_QC_HOLD",
  /**
   * D-340 ruling 2: e30's caller-keyed replay. A material issue's ledger leg
   * is keyed `work-order:{wo}:mi-<key>`, so the replay is judged on the leg's
   * `performed_by`. Another user, or the issuer through an impersonation
   * grant, presenting the key gets 409 with nothing from the recorded row, as
   * does the issuer presenting it with different material.
   */
  "IDEMPOTENCY_KEY_REUSE",
] as const;

export type ProductionProblemCode = (typeof productionProblemCodes)[number];
